import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { pipelineAPI } from '../../services/api';
import toast from 'react-hot-toast';
import IconPicker, { StageIcon } from '../../components/IconPicker';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const GripIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);

// Category badge colors
const categoryColors = {
  open: 'bg-blue-100 text-blue-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-800'
};

// Default colors for new stages
const stageColors = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
  '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6'
];

// Default icon for new stages (lucide icon name)
const DEFAULT_STAGE_ICON = 'ClipboardList';

const PipelineStages = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6',
    icon: DEFAULT_STAGE_ICON,
    category: 'open',
    probability: 0
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      setLoading(true);
      const [stagesRes, statsRes] = await Promise.all([
        pipelineAPI.getAll(),
        pipelineAPI.getStats().catch(() => ({ data: { data: null } }))
      ]);
      setStages(stagesRes.data.data || []);
      setStats(statsRes.data?.data || null);
    } catch (error) {
      console.error('Error fetching stages:', error);
      toast.error('Failed to load pipeline stages');
    } finally {
      setLoading(false);
    }
  };

  // Reorder stages on server
  const reorderStages = async (newStages) => {
    try {
      const reorderData = newStages.map((item, index) => ({
        id: item._id,
        order: index
      }));
      await pipelineAPI.reorder(reorderData);
      toast.success('Stages reordered');
    } catch (error) {
      console.error('Error reordering stages:', error);
      toast.error('Failed to save order');
      fetchStages(); // Revert on error
    }
  };

  // Move stage up
  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newStages = [...stages];
    [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
    setStages(newStages);
    await reorderStages(newStages);
  };

  // Move stage down
  const handleMoveDown = async (index) => {
    if (index === stages.length - 1) return;
    const newStages = [...stages];
    [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
    setStages(newStages);
    await reorderStages(newStages);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order locally first
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));
    setStages(updatedItems);

    // Save to server
    await reorderStages(updatedItems);
  };

  const openModal = (stage = null) => {
    if (stage) {
      setEditingStage(stage);
      setFormData({
        name: stage.name,
        color: stage.color,
        icon: stage.icon,
        category: stage.category,
        probability: stage.probability
      });
    } else {
      setEditingStage(null);
      setFormData({
        name: '',
        color: stageColors[stages.length % stageColors.length],
        icon: DEFAULT_STAGE_ICON,
        category: 'open',
        probability: 0
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStage(null);
    setFormData({
      name: '',
      color: '#3B82F6',
      icon: DEFAULT_STAGE_ICON,
      category: 'open',
      probability: 0
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Stage name is required');
      return;
    }

    try {
      setSaving(true);
      if (editingStage) {
        await pipelineAPI.update(editingStage._id, formData);
        toast.success('Stage updated successfully');
      } else {
        await pipelineAPI.create(formData);
        toast.success('Stage created successfully');
      }
      closeModal();
      fetchStages();
    } catch (error) {
      console.error('Error saving stage:', error);
      toast.error(error.response?.data?.message || 'Failed to save stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (stage) => {
    if (stage.isSystem) {
      toast.error('System stages cannot be deleted');
      return;
    }

    const leadsCount = stats?.find(s => s.key === stage.key)?.leadsCount || 0;
    if (leadsCount > 0) {
      toast.error(`Cannot delete. ${leadsCount} leads are using this stage.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${stage.name}"?`)) return;

    try {
      await pipelineAPI.delete(stage._id);
      toast.success('Stage deleted successfully');
      fetchStages();
    } catch (error) {
      console.error('Error deleting stage:', error);
      toast.error(error.response?.data?.message || 'Failed to delete stage');
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('This will reset all stages to defaults. Custom stages will be removed. Continue?')) return;

    try {
      await pipelineAPI.resetDefaults();
      toast.success('Stages reset to defaults');
      fetchStages();
    } catch (error) {
      console.error('Error resetting stages:', error);
      toast.error('Failed to reset stages');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Stages</h1>
          <p className="text-gray-600 mt-1">
            Customize your lead pipeline stages. Drag or use arrows to reorder.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleResetDefaults}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Reset to Defaults
          </button>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon />
            Add Stage
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && stats.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap gap-4">
            {stats.map((stage, index) => (
              <div
                key={stage._id}
                className="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
              >
                <StageIcon icon={stage.icon} className="w-4 h-4" />
                <span>{stage.name}</span>
                <span className="font-semibold">{stage.leadsCount}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stages List */}
      <div className="bg-white rounded-lg shadow">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="stages">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {stages.map((stage, index) => (
                  <Draggable
                    key={stage._id}
                    draggableId={stage._id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-gray-100 last:border-b-0 ${
                          snapshot.isDragging ? 'bg-indigo-50 shadow-lg' : 'bg-white'
                        } hover:bg-gray-50 transition-colors`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0"
                            title="Drag to reorder"
                          >
                            <GripIcon />
                          </div>

                          {/* Order Number */}
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600 flex-shrink-0">
                            {index + 1}
                          </div>

                          <div
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: stage.color }}
                          />
                          <StageIcon icon={stage.icon} className="w-5 h-5 text-primary-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 truncate">{stage.name}</span>
                              {stage.isSystem && (
                                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded flex-shrink-0">
                                  System
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className={`px-2 py-0.5 text-xs rounded ${categoryColors[stage.category]}`}
                              >
                                {stage.category}
                              </span>
                              <span className="text-xs text-gray-500">
                                {stage.probability}% win rate
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap justify-end sm:flex-nowrap">
                          {/* Leads Count */}
                          {stats && (
                            <span className="text-sm text-gray-500 px-2 py-1 bg-gray-100 rounded">
                              {stats.find(s => s.key === stage.key)?.leadsCount || 0} leads
                            </span>
                          )}

                          {/* Move Up/Down Buttons */}
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                              className={`p-1 rounded transition-colors ${
                                index === 0
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                              }`}
                              title="Move Up"
                            >
                              <ArrowUpIcon />
                            </button>
                            <button
                              onClick={() => handleMoveDown(index)}
                              disabled={index === stages.length - 1}
                              className={`p-1 rounded transition-colors ${
                                index === stages.length - 1
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                              }`}
                              title="Move Down"
                            >
                              <ArrowDownIcon />
                            </button>
                          </div>

                          {/* Edit Button */}
                          <button
                            onClick={() => openModal(stage)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <EditIcon />
                          </button>

                          {/* Delete Button */}
                          {!stage.isSystem && (
                            <button
                              onClick={() => handleDelete(stage)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {stages.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No pipeline stages found.</p>
            <button
              onClick={handleResetDefaults}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Load Default Stages
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-auto max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingStage ? 'Edit Stage' : 'Add New Stage'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stage Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., New Lead"
                    required
                  />
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {stageColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          formData.color === color ? 'border-gray-900' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                  </div>
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon
                  </label>
                  <IconPicker
                    value={formData.icon}
                    onChange={(icon) => setFormData({ ...formData, icon })}
                    color={formData.color}
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="open">Open (Active Leads)</option>
                    <option value="won">Won (Converted)</option>
                    <option value="lost">Lost (Disqualified)</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Probability */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Win Probability: {formData.probability}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.probability}
                    onChange={(e) =>
                      setFormData({ ...formData, probability: parseInt(e.target.value) })
                    }
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingStage ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineStages;